import { cloneElement } from 'react';
import UrlPattern from 'url-pattern';
import { AuthUserInfo } from '@bymason-private/auth-provider';
import { expandUrlParams, ExpandUrlParamsOptions } from '@bymason/utils';
import { DefaultSearchOptions, NavigateOptions, Route, RouteAndState, Routes } from './types';

export function deferDefaultRoute(defaultRoute: Route) {
  if (defaultRoute.viewComponent) {
    return defaultRoute;
  }

  return null;
}

export function getRouteAndState(path: string, routes: Routes): RouteAndState {
  const normalizedPath = path.split(`${process.env.PUBLIC_URL}`)[1];

  let state: Record<string, unknown> = {};

  const newRoute = Object.values(routes).find((route) => {
    const pattern = new UrlPattern(route.path);
    state = pattern.match(normalizedPath);

    return !!state;
  });

  if (history.state) {
    //
    // add missing history state
    //
    if (!state) {
      state = {};
    }
    Object.keys(history.state).forEach((key) => {
      if (!(key in state)) {
        state[key] = history.state[key];
      }
    });
  }

  return [newRoute || null, state];
}

export function getRouteComponent(route: Route, state = {}) {
  if (route.viewComponent) {
    return <>{cloneElement(route.viewComponent, state)}</>;
  }

  return null;
}

export function getSearchState<S = DefaultSearchOptions>(url: URL): S {
  const state: Record<string, string | string[]> = {};

  // automatically add querystring info to state
  const search = new URLSearchParams(url.search);
  search.forEach((value, key) => {
    if (!state[key]) {
      state[key] = value;
    } else {
      if (Array.isArray(state[key])) {
        (state[key] as string[]).push(value);
      } else {
        // more than one value for key detected, so
        // convert state for this key to an array
        const initialVal = state[key] as string;
        state[key] = [initialVal, value];
      }
    }
  });

  return (state as unknown) as S;
}

interface handleNavigationOptions<T> {
  authToken?: string;
  origin: string;
  route: Route | string;
  state?: T;
  userInfo?: AuthUserInfo;
  options?: NavigateOptions;
}

const defaultOptions: NavigateOptions = {
  newTab: false,
  replaceState: false,
};

export function handleNavigation<T>({
  origin,
  route,
  state,
  options,
}: handleNavigationOptions<T>): [newURL: URL, prevURL: URL] | undefined {
  const mergedOptions = { ...defaultOptions, ...options };
  const newPath = getPathFromRoute(route, state, options);

  // use expanded path to construct new url
  const newURL = new URL(`${origin}${newPath}`);
  const prevURL = new URL(window.location.href);

  if (mergedOptions.newTab) {
    window.open(newURL.toString(), '_blank');
    return;
  }

  if (window.location.href !== newURL.toString()) {
    // navigate browser history to new path
    if (mergedOptions.replaceState) {
      window.history.replaceState(state, '', newPath);
    } else {
      window.history.pushState(state, '', newPath);
    }

    // return previous URL
    return [newURL, prevURL];
  }

  return;
}

export function getPathFromRoute<T>(
  route: Route | string,
  state?: T,
  expandOptions?: ExpandUrlParamsOptions
) {
  const path = typeof route === 'string' ? route : route.path;
  let pathTemplate = `${process.env.PUBLIC_URL}${path.startsWith('/') ? '' : '/'}${path}`;

  if (typeof route !== 'string' && route.legacyPath) {
    pathTemplate = `${route.legacyPath.startsWith('/') ? '' : '/'}${route.legacyPath}`;
  }

  const origin = window.location.origin;
  const url = new URL(expandUrlParams(`${origin}${pathTemplate}`, state || {}, expandOptions));

  // expand path template using state to create new path
  return url.toString().replace(url.origin, '');
}
